import type { BlockedUser, CalendarShareGrant } from '@/api'
import { matchText } from '@/todos/fuzzy'

// From this many entries on, a list of the sharing settings shows a search field.
export const SHARING_SEARCH_MIN = 6

export interface CalendarGrantGroup {
  calendarId: number
  name: string
  grants: CalendarShareGrant[]
}

export interface GranteeGroup {
  key: string
  // (one of the grants, for its label)
  sample: CalendarShareGrant
  grants: CalendarShareGrant[]
}

// Who a grant is for, as a stable key for the URL: `user:<id>`, `chat:<id>`, `everyone` or `any_group`.
export function granteeKey(grant: CalendarShareGrant): string {
  if (grant.permission_type === 'specific_user') return `user:${grant.target_telegram_user_id ?? grant.target_username ?? ''}`
  if (grant.permission_type === 'specific_chat') return `chat:${grant.target_chat_id ?? ''}`
  return grant.permission_type
}

export function groupByCalendar(grants: CalendarShareGrant[]): CalendarGrantGroup[] {
  const groups = new Map<number, CalendarGrantGroup>()
  for (const grant of grants) {
    const group = groups.get(grant.caldav_calendar_id)
    if (group) group.grants.push(grant)
    else groups.set(grant.caldav_calendar_id, { calendarId: grant.caldav_calendar_id, name: grant.calendar_name, grants: [grant] })
  }
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name) || a.calendarId - b.calendarId)
}

// `label` is the text shown for a grantee, which the groups are sorted by.
export function groupByGrantee(grants: CalendarShareGrant[], label: (grant: CalendarShareGrant) => string): GranteeGroup[] {
  const groups = new Map<string, GranteeGroup>()
  for (const grant of grants) {
    const key = granteeKey(grant)
    const group = groups.get(key)
    if (group) group.grants.push(grant)
    else groups.set(key, { key, sample: grant, grants: [grant] })
  }
  return [...groups.values()].sort((a, b) => label(a.sample).localeCompare(label(b.sample)) || a.key.localeCompare(b.key))
}

// The items a search keeps, best match first (a tier is how `matchText` matched: prefix, somewhere, letters in order); ties and an
// empty search keep the order of `items`.
export function searchBy<T>(items: T[], query: string, texts: (item: T) => string[]): T[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return items
  const ranked: { item: T; rank: number; index: number }[] = []
  items.forEach((item, index) => {
    const ranks = texts(item)
      .map((text) => (text ? matchText(text, needle)?.tier : undefined))
      .filter((tier): tier is number => tier !== undefined)
    if (ranks.length) ranked.push({ item, rank: Math.min(...ranks), index })
  })
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index)
  return ranked.map((entry) => entry.item)
}

export function searchBlocked(blocked: BlockedUser[], query: string): BlockedUser[] {
  return searchBy(blocked, query, (b) => [b.blocked_username ?? '', String(b.blocked_telegram_user_id)])
}
